import { Alert, Box, Button, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FormEvent } from 'react';
import { UPDATE_ONE_FIELD } from '@app/shared';
import type { UnitDetailsInput, UnitDetailsResult, UnitKind } from '@app/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ApiError, api } from '../../lib/api';
import { useCarriers } from '../plans/lookups';
import { BulkScreen } from './BulkPage';
import { useProductModelOptions } from './api';
import { BULK_CONFIGS } from './bulkConfig';
import { productLabel } from './labels';

type UploadType = 'single' | 'bulk';

/** A field of a single-unit form; `max` is the reference's length. */
interface DetailField {
  key: string;
  label: string;
  max?: number;
  input?: 'carrier' | 'model' | 'byod' | 'money';
}

/**
 * The single-unit forms in the reference's order (walk of 2026-10-15). Vendor and Shipper ID
 * are left out: there are no vendor or shipper lists here.
 */
const FIELDS: Record<UnitKind, DetailField[]> = {
  sim: [
    { key: 'identifier', label: 'ESN/SIM', max: 25 },
    { key: 'carrierId', label: 'Carrier', input: 'carrier' },
    { key: 'modelId', label: 'Model', input: 'model' },
    { key: 'puk2', label: 'MSL/PUK', max: 10 },
    { key: 'msid', label: 'MSID', max: 10 },
    { key: 'poNumber', label: 'PO', max: 50 },
    { key: 'box', label: 'Box', max: 20 },
    { key: 'wholesalePriceCents', label: 'Wholesale Price', max: 6, input: 'money' },
    { key: 'sellingPriceCents', label: 'Selling Price', max: 6, input: 'money' },
    { key: 'notes', label: 'Notes', max: 30 },
    { key: 'uiccid', label: 'UICCID', max: 25 },
    { key: 'activationFeeCents', label: 'Activation Fee', max: 6, input: 'money' },
    { key: 'byod', label: 'BYOD', input: 'byod' },
    { key: 'deviceId', label: 'Device ID', max: 25 },
  ],
  device: [
    { key: 'identifier', label: 'Device ID', max: 25 },
    { key: 'modelId', label: 'Model', input: 'model' },
    { key: 'byod', label: 'BYOD', input: 'byod' },
    { key: 'notes', label: 'Notes', max: 30 },
    { key: 'wholesalePriceCents', label: 'Wholesale Price', max: 6, input: 'money' },
    { key: 'carrierId', label: 'Carrier', input: 'carrier' },
    { key: 'poNumber', label: 'PO', max: 50 },
    { key: 'box', label: 'Box', max: 20 },
    { key: 'retailPriceCents', label: 'Device Retail Price', max: 8, input: 'money' },
    { key: 'acpCoPayCents', label: 'ACP Co-Pay Amount', max: 10, input: 'money' },
    {
      key: 'acpReimbursementCents',
      label: 'ACP Device Reimbursement Amount',
      max: 10,
      input: 'money',
    },
  ],
};

const NOUN: Record<UnitKind, string> = { sim: 'ESN/SIM', device: 'Device' };
const MONEY = /^\d+(\.\d{1,2})?$/;

/** "Single Unit": the fields filled in replace the stored ones; empty fields are kept. */
function SingleUnitForm({ kind }: { kind: UnitKind }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const carriers = useCarriers();
  const models = useProductModelOptions();
  const [values, setValues] = useState<Record<string, string>>({});
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [note, setNote] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: UnitDetailsInput) =>
      api.post<UnitDetailsResult>('/inventory/unit-details', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inventory'] }),
  });

  const fields = FIELDS[kind];
  const set = (key: string, value: string) => {
    setValues((v) => ({ ...v, [key]: value }));
    setProblems((p) => ({ ...p, [key]: '' }));
    setNote(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    const body: Record<string, unknown> = { kind };
    for (const f of fields) {
      const value = (values[f.key] ?? '').trim();
      if (!value) continue;
      if (f.input === 'money') {
        if (MONEY.test(value)) body[f.key] = Math.round(Number(value) * 100);
        else found[f.key] = `${f.label} must be an amount in dollars`;
      } else body[f.key] = value;
    }
    if (!body.identifier) found.identifier = `${NOUN[kind]} is required`;
    setProblems(found);
    if (Object.keys(found).length) return;
    if (Object.keys(body).length === 2) {
      setNote(UPDATE_ONE_FIELD[kind]);
      return;
    }
    try {
      const result = await save.mutateAsync(body as UnitDetailsInput);
      notify.success(
        result.changed.length
          ? `${NOUN[kind]} ${String(body.identifier)} updated: ${result.changed.join(', ')}.`
          : `${NOUN[kind]} ${String(body.identifier)}: nothing to change.`,
      );
      setValues({});
    } catch (err) {
      const fromServer: Record<string, string> = {};
      if (err instanceof ApiError)
        for (const d of err.details)
          if (fields.some((f) => f.key === d.path)) fromServer[d.path] = d.message;
      if (Object.keys(fromServer).length) setProblems(fromServer);
      else notify.error(err);
    }
  };

  const field = (f: DetailField) => {
    const common = {
      key: f.key,
      label: f.label,
      value: values[f.key] ?? '',
      onChange: (e: { target: { value: string } }) => set(f.key, e.target.value),
      error: Boolean(problems[f.key]),
      helperText: problems[f.key] || undefined,
      required: f.key === 'identifier',
    };
    if (f.input === 'carrier' || f.input === 'model' || f.input === 'byod') {
      const options =
        f.input === 'carrier'
          ? (carriers.data ?? []).map((c) => ({ value: c.id, label: `${c.code} - ${c.name}` }))
          : f.input === 'model'
            ? (models.data ?? []).map((m) => ({ value: m.id, label: productLabel(m) }))
            : [
                { value: 'Y', label: 'Y' },
                { value: 'N', label: 'N' },
              ];
      return (
        <TextField select {...common}>
          <MenuItem value="">
            <em>{f.input === 'byod' ? 'SELECT HERE' : '-- Select --'}</em>
          </MenuItem>
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    return (
      <TextField
        {...common}
        slotProps={{ htmlInput: { maxLength: f.max } }}
        placeholder={f.input === 'money' ? '0.00' : undefined}
      />
    );
  };

  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      onSubmit={(e) => void submit(e)}
      sx={{ p: 2 }}
      aria-label={`${NOUN[kind]} Single Unit`}
    >
      <Box
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}
      >
        {fields.map(field)}
      </Box>
      <Typography color={note ? 'error' : 'text.secondary'} sx={{ mt: 2 }}>
        Note : {UPDATE_ONE_FIELD[kind]}
      </Typography>
      {note ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {note}
        </Alert>
      ) : null}
      <Box sx={{ mt: 2 }}>
        <Button type="submit" variant="contained" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Submit'}
        </Button>
      </Box>
    </Paper>
  );
}

/**
 * Update Inventory (PC391), reached from the Manage Inventory tiles: "Update Feature" (ESN/SIM
 * or DEVICE) and "Upload Type" (Single Unit or Bulk Upload) choose the form, as the reference.
 */
export function UpdateInventoryPage() {
  // The tab strip of the Inventory Dashboard opens this page with both choices made.
  const [params] = useSearchParams();
  const [feature, setFeature] = useState<UnitKind | ''>(
    params.get('feature') === 'sim' || params.get('feature') === 'device'
      ? (params.get('feature') as UnitKind)
      : '',
  );
  const [upload, setUpload] = useState<UploadType | ''>(
    params.get('upload') === 'single' || params.get('upload') === 'bulk'
      ? (params.get('upload') as UploadType)
      : '',
  );
  return (
    <Box>
      <PageHeader title="Update Inventory" code="PC391" />
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}
        >
          <TextField
            select
            required
            label="Update Feature"
            value={feature}
            onChange={(e) => setFeature(e.target.value as UnitKind)}
          >
            <MenuItem value="">
              <em>-- Select --</em>
            </MenuItem>
            <MenuItem value="sim">ESN/SIM</MenuItem>
            <MenuItem value="device">DEVICE</MenuItem>
          </TextField>
          <TextField
            select
            required
            label="Upload Type"
            value={upload}
            onChange={(e) => setUpload(e.target.value as UploadType)}
          >
            <MenuItem value="">
              <em>-- Select --</em>
            </MenuItem>
            <MenuItem value="single">Single Unit</MenuItem>
            <MenuItem value="bulk">Bulk Upload</MenuItem>
          </TextField>
        </Box>
      </Paper>
      {feature && upload === 'single' ? <SingleUnitForm key={feature} kind={feature} /> : null}
      {feature && upload === 'bulk' ? (
        <BulkScreen
          key={feature}
          embedded
          config={BULK_CONFIGS[feature === 'sim' ? 'update-sims' : 'update-devices']}
        />
      ) : null}
    </Box>
  );
}
